import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Search, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { EASE } from "@/components/Reveal";
import { scrollToTop } from "@/lib/lenis";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
];

export const Header = ({ onSearch }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const { count, setOpen } = useCart();
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => { setMenu(false); }, [pathname]);

  return (
    <header
      data-testid="main-header"
      className={`sticky top-0 z-40 border-b transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || menu ? "border-[color:var(--line)] bg-[#F4F2EE]/75 backdrop-blur-xl" : "border-transparent bg-[#F4F2EE]"
      }`}
    >
      <div className="mx-auto grid h-16 grid-cols-3 items-center px-4 md:h-[72px] md:px-8">
        <nav className="flex items-center gap-8" aria-label="Primary">
          <button data-testid="mobile-menu-toggle" aria-label={menu ? "Close menu" : "Open menu"} onClick={() => setMenu((m) => !m)} className="-ml-2 grid h-11 w-11 place-items-center md:hidden">
            {menu ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => n.to === pathname && scrollToTop()} data-testid={`nav-${n.label.toLowerCase()}`} className="label draw-link hidden py-2 transition-colors duration-300 hover:text-[#E2541B] md:inline-block">
              {n.label}
            </NavLink>
          ))}
        </nav>
        <Link to="/" data-testid="header-logo" aria-label="Sviin home" className="justify-self-center">
          <span className="font-['Pinyon_Script',cursive] text-[34px] leading-none md:text-[42px]">Sviin</span>
        </Link>
        <div className="flex items-center justify-self-end">
          <button data-testid="search-open-button" aria-label="Search" onClick={onSearch} className="grid h-11 w-11 place-items-center transition-colors hover:text-[#E2541B]">
            <Search size={19} strokeWidth={1.5} />
          </button>
          <button data-testid="cart-open-button" aria-label={`Cart, ${count} items`} onClick={() => setOpen(true)} className="label -mr-2 flex h-11 items-center gap-2 px-2 transition-colors hover:text-[#E2541B]">
            <span className="hidden sm:inline">Cart</span>
            <span data-testid="cart-count-badge" className={`grid h-5 min-w-5 place-items-center px-1 text-[10px] tracking-normal transition-colors ${count ? "bg-[#E2541B] text-white" : "border border-[#111111]/30"}`}>
              {count}
            </span>
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="overflow-hidden border-t md:hidden"
          >
            <div className="flex flex-col px-4 py-6">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i + 0.1, duration: 0.5, ease: EASE }}>
                  <Link to={n.to} onClick={() => { if (n.to === pathname) { setMenu(false); scrollToTop(); } }} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className="font-display block py-2 text-5xl font-bold tracking-[-0.05em]">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
